import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Ruler, Mic2, Projector, Sun, Wine, Trees, Accessibility, ChevronLeft, ChevronRight, Building2, Briefcase, PartyPopper, UsersRound, Shuffle, Heart, Sparkles, Flame } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BRAND } from "@/lib/brand";
import { resolveImageUrl } from "@/lib/api";

const EQUIP_ICONS = [Sun, Accessibility, Wine, Trees, Mic2, Projector];
const AUDIENCE_ICONS = [Building2, Briefcase, PartyPopper, UsersRound];
const DNA_ICONS = [Shuffle, Heart, Sparkles, Flame];
const AUTOPLAY_MS = 4500;

function VenueCarousel() {
  const images = BRAND.carousel;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => {
      setIndex((i) => (i + 1) % images.length);
    }, AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [index, paused, images.length]);

  const go = (delta) => {
    setIndex((i) => (i + delta + images.length) % images.length);
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="venue-carousel"
    >
      <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_25px_50px_rgb(42,36,33,0.18)] bg-[#eae0df]">
        <AnimatePresence mode="wait">
          <motion.img
            key={images[index]}
            src={resolveImageUrl(images[index])}
            alt={`Aura sala ${index + 1}`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            data-testid={`venue-carousel-img-${index}`}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#2e5949]/30 via-transparent to-transparent pointer-events-none" />

        {/* Manual prev/next */}
        <button
          onClick={() => go(-1)}
          aria-label="prev"
          data-testid="venue-carousel-prev"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#fdfcf9]/85 backdrop-blur flex items-center justify-center text-[#3e664c] hover:bg-[#fdfcf9] transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={() => go(1)}
          aria-label="next"
          data-testid="venue-carousel-next"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#fdfcf9]/85 backdrop-blur flex items-center justify-center text-[#3e664c] hover:bg-[#fdfcf9] transition-colors"
        >
          <ChevronRight size={18} />
        </button>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="venue-carousel-dots">
          {images.map((url, i) => (
            <button
              key={url}
              onClick={() => setIndex(i)}
              aria-label={`slide ${i + 1}`}
              data-testid={`venue-carousel-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-7 bg-[#fdfcf9]" : "w-1.5 bg-[#fdfcf9]/55 hover:bg-[#fdfcf9]/80"
              }`}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#fdfcf9]/20 overflow-hidden">
          {!paused && (
            <motion.div
              key={`progress-${index}`}
              className="h-full bg-[#E2C9A0]"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function VenuePresentation() {
  const { t } = useI18n();
  return (
    <section id="sala" data-testid="venue-presentation" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Carousel half */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <VenueCarousel />
        </motion.div>

        {/* Info half */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-6" data-testid="venue-eyebrow">
            {t.venue.eyebrow}
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.18] pt-1">
            {t.venue.title}
          </h2>
          <p className="mt-8 text-[#5C544F] leading-relaxed max-w-md">{t.venue.desc}</p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[#eae0df] p-5 bg-white" data-testid="venue-capacity">
              <div className="w-10 h-10 rounded-full bg-[#3e664c]/10 flex items-center justify-center mb-3">
                <Users size={18} className="text-[#3e664c]" />
              </div>
              <p className="uppercase tracking-[0.2em] text-[10px] text-[#5C544F]">{t.venue.capacityTitle}</p>
              <p className="font-display text-2xl mt-1 leading-tight">{t.venue.capacityValue}</p>
            </div>
            <div className="rounded-2xl border border-[#eae0df] p-5 bg-white" data-testid="venue-area">
              <div className="w-10 h-10 rounded-full bg-[#3e664c]/10 flex items-center justify-center mb-3">
                <Ruler size={18} className="text-[#3e664c]" />
              </div>
              <p className="uppercase tracking-[0.2em] text-[10px] text-[#5C544F]">{t.venue.areaTitle}</p>
              <p className="font-display text-2xl mt-1 leading-tight">{t.venue.areaValue}</p>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-[#eae0df] p-6 bg-white" data-testid="venue-equipment">
            <p className="uppercase tracking-[0.22em] text-[10px] text-[#5C544F] mb-4">{t.venue.equipTitle}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {t.venue.equipList.map((item, idx) => {
              const Icon = EQUIP_ICONS[idx] || Sun;
              return (
                <li key={item} className="flex items-start gap-3" data-testid={`venue-equip-${idx}`}>
                    <Icon size={16} className="text-[#3e664c] mt-0.5 shrink-0" />
                    <span className="text-[#2A2421]">{item}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <a
            href="#disponibilidad"
            data-testid="venue-cta-calendar"
            className="mt-8 inline-flex items-center gap-3 px-7 py-3 rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] text-sm uppercase tracking-[0.18em] transition-colors"
          >
            {t.cta.seeCalendar}
          </a>
        </motion.div>
      </div>

      {/* Audience block — Per a qui? */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-7xl mx-auto mt-24 md:mt-32"
        data-testid="venue-audience"
      >
        <div className="max-w-2xl mb-12">
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-4 font-medium">{t.venue.audienceEyebrow}</p>
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] uppercase leading-[1.08]">
            {t.venue.audienceTitle}
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.venue.audiences.map((a, idx) => {
            const Icon = AUDIENCE_ICONS[idx] || Building2;
            return (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                whileHover={{ y: -4 }}
                className="rounded-2xl border border-[#eae0df] bg-white p-6 transition-shadow hover:shadow-[0_18px_35px_rgb(28,36,33,0.08)]"
                data-testid={`venue-audience-${idx}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#5daa92]/15 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#3e664c]" />
                </div>
                <p className="font-display text-xl tracking-tight mb-2">{a.name}</p>
                <p className="text-sm text-[#5C544F] leading-relaxed">{a.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* DNA block */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-7xl mx-auto mt-24 md:mt-32"
        data-testid="venue-dna"
      >
        <div className="rounded-3xl bg-gradient-to-br from-[#3e664c] to-[#2e5949] text-[#fdfcf9] p-8 md:p-14 relative overflow-hidden">
          <div className="max-w-2xl mb-10 relative">
            <p className="uppercase tracking-[0.28em] text-xs text-[#dfeabf] mb-4 font-medium">{t.venue.dnaEyebrow}</p>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] uppercase leading-[1.08]">
              {t.venue.dnaTitle}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            {t.venue.dnaValues.map((v, idx) => {
              const Icon = DNA_ICONS[idx] || Shuffle;
              return (
                <motion.div
                  key={v.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="rounded-2xl bg-[#fdfcf9]/10 border border-[#fdfcf9]/15 p-6 backdrop-blur-sm"
                  data-testid={`venue-dna-${idx}`}
                >
                  <Icon size={22} className="text-[#dfeabf] mb-4" />
                  <p className="font-display text-xl tracking-tight mb-2 text-[#fdfcf9]">{v.name}</p>
                  <p className="text-sm text-[#fdfcf9]/80 leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
